import React from 'react';

import { InternalLink } from '../../../../../../components/Clickables/InternalLink/InternalLink';
import { FishingWaterMetadataPills } from '../../../../../../components/FishingWaterMetadataPills/FishingWaterMetadataPills';
import { Heading } from '../../../../../../components/Heading/Heading';
import { Rating, RatingVariant } from '../../../../../../components/Rating/Rating';
import { Text } from '../../../../../../components/Text/Text';
import { useGeolocation } from '../../../../../../contexts/GeolocationContext/GeolocationContext';
import { trackEvent } from '../../../../../../helpers/analytics/trackEvent';
import { convertDistance } from '../../../../../../helpers/conversion';
import { useUserInfo } from '../../../../../../helpers/hooks/useUserInfo';
import { getDistance } from '../../../../../../helpers/location/getDistance';
import { abbreviateNumber } from '../../../../../../helpers/numbers/abbreviateNumber';
import { getFishingWaterUrl } from '../../../../../../helpers/urls';
import { GetFishingWaterSummary_fishingWater_metadata } from '../../../../../../interfaces/graphql';

interface IProps {
  displayName: string;
  region: string | undefined;
  country: string | undefined;
  totalAverageRating: number;
  totalRatingCount: number;
  catchesCount: number;
  longitude: number;
  latitude: number;
  externalId: string;
  metadata: GetFishingWaterSummary_fishingWater_metadata[];
  fishingForbidden: boolean;
}

export const FishingWaterViewHeader = ({
  displayName,
  region,
  country,
  totalAverageRating,
  totalRatingCount,
  catchesCount,
  longitude,
  latitude,
  externalId,
  metadata,
  fishingForbidden,
}: IProps) => {
  const { location } = useGeolocation();
  const { user } = useUserInfo();

  return (
    <div className="flex h-full flex-col pt-2">
      <Heading variant="xs">{displayName}</Heading>

      <div>
        <Text as="span" variant="sm" className="font-heading">
          {region && country ? `${region}, ${country}` : ''}
          {region && !country ? region : ''}
          {!region && country ? country : ''}
        </Text>
        {!fishingForbidden && totalRatingCount > 0 && (
          <Rating
            textSize="sm"
            rating={totalAverageRating}
            numberOfReviews={totalRatingCount}
            variant={RatingVariant.singleStar}
          />
        )}
      </div>

      <FishingWaterMetadataPills metadata={metadata} outerClassName="mt-4" />

      {!fishingForbidden && (
        <div>
          <Text as="div" variant="md" className="font-heading mt-4">
            {catchesCount !== 0 ? (
              <span>
                {abbreviateNumber(catchesCount)}
                {catchesCount === 1 ? ' logged catch ' : ' logged catches '}
              </span>
            ) : (
              <span>No logged catches</span>
            )}
          </Text>
          <Text>
            {location?.coordinates.longitude && location.coordinates.latitude ? (
              ` ${convertDistance(getDistance(location.coordinates, { latitude, longitude }), user)} from
            your location`
            ) : (
              <></>
            )}
          </Text>
        </div>
      )}

      <div className={`flex justify-between pb-2 ${fishingForbidden ? 'mt-6' : ''}`}>
        <InternalLink
          to={getFishingWaterUrl(externalId, displayName)}
          size="sm"
          variant="buttonPrimary"
          onClick={() => {
            void trackEvent({
              name: 'mykiss_explore_cta_clicked',
              properties: {
                type: 'fishing_water',
                target_id: externalId,
                target: 'fishing_water',
              },
            });
          }}
        >
          Read more
        </InternalLink>
      </div>
    </div>
  );
};
